import { AfterViewInit, computed, Directive, ElementRef, OnDestroy, OnInit, Signal, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { NavigationExtras, Router } from '@angular/router';
import { ErrorDefinition, ErrorDetails } from '@core/model/errorSummary.model';
import { Establishment } from '@core/model/establishment.model';
import { URLStructure } from '@core/model/url.model';
import { BackService } from '@core/services/back.service';
import { ErrorSummaryService } from '@core/services/error-summary.service';
import { EstablishmentService } from '@core/services/establishment.service';
import { ProgressBarUtil } from '@core/utils/progress-bar-util';
import { isNull, isEqual } from 'lodash';
import { Subscription } from 'rxjs';

@Directive()
export class WorkplaceQuestion implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('formEl') formEl: ElementRef;
  public form: UntypedFormGroup;
  public establishment: Establishment;
  public primaryWorkplace: Establishment;
  public submitted = false;

  public return: URLStructure;

  private _previousQuestionPage: string;
  private _nextQuestionPage: string;
  private _skipToQuestionPage: string;

  public back: URLStructure;
  public hideBackLink: boolean;

  public formErrorsMap: Array<ErrorDetails> = [];
  public serverError: string;
  public serverErrorsMap: Array<ErrorDefinition> = [];
  protected subscriptions: Subscription = new Subscription();
  protected initiated = false;
  public submitAction: { action: string; save: boolean } = null;
  public workplaceFlowSections: string[] = ProgressBarUtil.workplaceFlowProgressBarSections();
  public isAtEndOfPayAndPensionsMiniFlow: boolean = false;

  private _isInAddDetailsFlow: Signal<boolean> = computed(() => {
    return this.router.url.includes('add-workplace-details');
  });

  private _visitedFromWorkplaceSummary: Signal<boolean> = computed(() => {
    const returnUrlMatchWorkplaceSummary =
      this.return && isEqual(this.return, { url: ['/dashboard'], fragment: 'workplace' });
    return this.router.url.includes('workplace-summary') || returnUrlMatchWorkplaceSummary;
  });

  constructor(
    protected formBuilder: UntypedFormBuilder,
    protected router: Router,
    protected backService: BackService,
    protected errorSummaryService: ErrorSummaryService,
    protected establishmentService: EstablishmentService,
  ) {}

  ngOnInit() {
    this.return = this.establishmentService.returnTo;

    this.subscriptions.add(
      this.establishmentService.establishment$.subscribe((establishment) => {
        this.establishment = establishment;
        this.primaryWorkplace = this.establishmentService.primaryWorkplace;

        if (!this.initiated) {
          this._init();

          if (!this.hideBackLink) {
            this.setBackLink();
          }
        }
      }),
    );

    this.setupFormErrorsMap();
    this.setupServerErrorsMap();
  }

  ngAfterViewInit() {
    this.errorSummaryService.formEl$.next(this.formEl);
  }

  protected setBackLink(): void {
    if (this.return) {
      this.back = this.return;
    } else {
      this.back = this.isInAddDetailsFlow
        ? { url: this.previousRoute }
        : { url: ['/dashboard'], fragment: 'workplace' };
    }
    this.backService.setBackLink(this.back);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  public getFormErrorMessage(item: string, errorType: string): string {
    return this.errorSummaryService.getFormErrorMessage(item, errorType, this.formErrorsMap);
  }

  public getFirstErrorMessage(item: string): string {
    const errorType = Object.keys(this.form.get(item).errors)[0];
    return this.errorSummaryService.getFormErrorMessage(item, errorType, this.formErrorsMap);
  }

  protected set previousQuestionPage(pageName: string) {
    this._previousQuestionPage = pageName;
  }

  protected set nextQuestionPage(pageName: string) {
    this._nextQuestionPage = pageName;
  }

  protected set skipToQuestionPage(pageName: string) {
    this._skipToQuestionPage = pageName;
  }

  protected get previousQuestionPage(): string {
    return this._previousQuestionPage;
  }

  protected get nextQuestionPage(): string {
    return this._nextQuestionPage;
  }

  protected get skipToQuestionPage(): string {
    return this._skipToQuestionPage;
  }

  public get isInAddDetailsFlow(): boolean {
    return this._isInAddDetailsFlow();
  }

  public get visitedFromWorkplaceSummary(): boolean {
    return this._visitedFromWorkplaceSummary();
  }

  private get baseRoute(): string[] {
    if (this.visitedFromWorkplaceSummary) {
      return this.establishmentService.baseRouteForWorkplaceSummary(this.establishment.uid);
    } else if (this.isInAddDetailsFlow) {
      return this.establishmentService.baseRouteForAddWorkplaceDetails(this.establishment.uid);
    } else {
      return ['/workplace', this.establishment.uid];
    }
  }

  public get previousRoute(): string[] {
    return [...this.baseRoute, this._previousQuestionPage];
  }

  public get nextRoute(): string[] {
    return [...this.baseRoute, this._nextQuestionPage];
  }

  public get skipRoute(): string[] {
    return [...this.baseRoute, this._skipToQuestionPage];
  }

  protected navigateToQuestionPage(pathSegment: string, ...extras: [NavigationExtras?]): Promise<boolean> {
    const destinationUrl = [...this.baseRoute, pathSegment];
    return this.router.navigate(destinationUrl, ...extras);
  }

  protected handleSkipAction(): Promise<boolean> {
    if (this.isAtEndOfPayAndPensionsMiniFlow) {
      this.router
        .navigate(this.return.url, {
          fragment: this.return.fragment,
          queryParams: this.return.queryParams,
        })
        .then(() => this.addAlert());
      return;
    }
    return this.router.navigate(this.skipRoute);
  }

  protected navigate(): Promise<boolean> {
    const action = this.submitAction.action;

    if (!action) {
      return;
    }

    switch (action) {
      case 'continue':
        return this.router.navigate(this.nextRoute);
      case 'summary':
        return this.router.navigate(['/dashboard'], { fragment: 'workplace' });
      case 'skip':
        return this.handleSkipAction();
      case 'exit':
        return this.router.navigate(['/dashboard'], { fragment: 'workplace' });
      case 'return':
        return this.router.navigate(this.return.url, {
          fragment: this.return.fragment,
          queryParams: this.return.queryParams,
        });
    }
  }

  public setSubmitAction(payload: { action: string; save: boolean }): void {
    this.submitAction = { action: payload.action, save: payload.save };

    if (!payload.save) {
      this.onSubmit();
    }
  }

  public onSubmit(): void {
    if (!this.submitAction.save) {
      this.establishment.showSharingPermissionsBanner
        ? this.removeSharingPermissionsBanner(() => this.navigate())
        : this.navigate();
      return;
    }

    this.serverError = null;
    this.submitted = true;
    this.errorSummaryService.syncFormErrorsEvent.next(true);

    /**
     * Required to reinstate error links on dynamic forms
     * where the error summary is removed on adding rows
     */
    this.addErrorLinkFunctionality();
    this.createDynamicErrorMessaging();

    if (!this.form.valid) {
      this.errorSummaryService.scrollToErrorSummary();
      return;
    }

    const props = this.generateUpdateProps();

    if (isNull(props)) {
      this.onSuccess();
      if (this.isAtEndOfPayAndPensionsMiniFlow) {
        this.navigate().then(() => {
          this.addAlert();
        });
      } else {
        this.navigate();
      }
      return;
    }

    this.updateEstablishment(props);
  }

  protected _init(): void {
    this.initiated = true;
    this.init();
  }

  protected init(): void {}
  protected setupFormErrorsMap(): void {}
  protected setupServerErrorsMap(): void {}
  protected generateUpdateProps(): any {}
  protected updateEstablishment(props): void {}
  protected onSuccess(): void {}
  protected removeSharingPermissionsBanner(completeFunction: () => unknown): void {
    // callback is invoked if func not declared in child to ensure navigation
    completeFunction();
  }
  protected createDynamicErrorMessaging(): void {}
  protected addErrorLinkFunctionality(): void {}
  protected addAlert(): void {}

  protected _onSuccess(data) {
    this.establishmentService.setState({ ...this.establishment, ...data });
    this.onSuccess();

    this.navigate().then(() => {
      this.addAlert();
    });
  }

  protected onError(error) {
    this.serverError = this.errorSummaryService.getServerErrorMessage(error.status, this.serverErrorsMap);
    this.errorSummaryService.scrollToErrorSummary();
  }

  public get isPrimaryWorkplace() {
    return this.primaryWorkplace && this.establishment.uid === this.primaryWorkplace.uid;
  }
}
